/**
 * SynthFactory.js
 * Generates specific Tone.js synth chains based on Instrument Definitions.
 * implements sound design logic migrated from Legacy MidiEngine.
 */

import * as Tone from 'tone';

/**
 * Wrapper class to standardize synth output chains.
 * Encapsulates the source synth and the output node (end of chain).
 * Ensures proper disposal of the entire effect chain.
 */
class SynthWrapper {
    constructor(synth, chain = []) {
        this.synth = synth;
        this.chain = chain;
        this.output = chain.length > 0 ? chain[chain.length - 1] : synth;
    }

    connect(destination) {
        this.output.connect(destination);
        return this;
    }

    disconnect(destination) {
        if (destination) {
            this.output.disconnect(destination);
        } else {
            this.output.disconnect();
        }
        return this;
    }

    dispose() {
        if (this.synth) this.synth.dispose();
        if (this.chain) {
            this.chain.forEach(node => {
                if (node && !node.disposed) node.dispose();
            });
        }
    }

    // Proxy methods to source synth
    triggerAttackRelease(...args) {
        if (this.synth.triggerAttackRelease) {
            this.synth.triggerAttackRelease(...args);
        }
    }

    triggerAttack(...args) {
        if (this.synth.triggerAttack) {
            this.synth.triggerAttack(...args);
        }
    }

    triggerRelease(...args) {
        if (this.synth.triggerRelease) {
            this.synth.triggerRelease(...args);
        }
    }

    releaseAll(...args) {
         if (this.synth.releaseAll) {
            this.synth.releaseAll(...args);
        }
    }

    get volume() {
        return this.synth.volume;
    }

    get disposed() {
        return this.synth.disposed;
    }

    // In case volume is set directly
    set volume(val) {
        this.synth.volume = val;
    }

    setArticulation(articulation) {
        // Real-time Envelope Modification for Articulation
        const synthToMod = this.synth.voices ? this.synth.options : this.synth; // PolySynth vs Mono

        // Helper to safely set envelope if it exists
        const setEnv = (target, env) => {
            if (target && target.envelope) {
                // If PolySynth, we might need to set on 'options' or iterate voices?
                // Tone.PolySynth.set() works for global params.
                if (this.synth.set) {
                    this.synth.set({ envelope: env });
                } else {
                    // MonoSynth
                    Object.assign(target.envelope, env);
                }
            }
        };

        if (articulation === 'staccato' || articulation === 'spiccato' || articulation === 'short') {
            // Snappy
            if (this.synth.set) {
                this.synth.set({
                    envelope: { attack: 0.005, decay: 0.1, sustain: 0.0, release: 0.1 }
                });
            }
        } else if (articulation === 'legato' || articulation === 'long') {
            // Smooth
            // Revert to "standard" defaults (hard to know original, but safe assumption)
            if (this.synth.set) {
                this.synth.set({
                    envelope: { attack: 0.1, decay: 0.3, sustain: 0.8, release: 1.0 }
                });
            }
        } else if (articulation === 'marcato' || articulation === 'accent') {
            // Hard Attack
            if (this.synth.set) {
                this.synth.set({
                    envelope: { attack: 0.001, decay: 0.2, sustain: 0.6, release: 0.5 }
                });
            }
        }

        // Proxy to custom method if exists
        if (this.synth.setArticulation) {
            this.synth.setArticulation(articulation);
        }
    }
}

/**
 * Creates a Tone.Sampler for the instrument.
 * Relying on Tone.js internal buffer caching to avoid reloading samples.
 */
const getSamplerForInstrument = (instrumentDef, isMobile) => {
    if (!instrumentDef || !instrumentDef.sampleMap) return null;

    // Resolve URL map. Support nested 'default' or direct map.
    let urls = instrumentDef.sampleMap.default || instrumentDef.sampleMap;
    // Fallback: if 'long' or 'sustain' exists, use that
    if (!urls && instrumentDef.sampleMap.long) urls = instrumentDef.sampleMap.long;
    if (!urls && instrumentDef.sampleMap.sustain) urls = instrumentDef.sampleMap.sustain;

    if (!urls) return null;

    const sampler = new Tone.Sampler({
        urls: urls,
        baseUrl: "", // URLs are absolute in our map
        release: 1,
        onload: () => {
            // console.log(`[SynthFactory] Samples loaded for ${instrumentDef.id}`);
        }
    });

    // Standardize volume
    sampler.volume.value = -10;

    // Filter removed (Moved to ChannelStrip mixTraits.eq)
    // Returning wrapper with no extra chain
    return new SynthWrapper(sampler, []);
};

// Factory Function
export const getSynthForInstrument = (instrumentDef, role, isMobile) => {
    // 0. Check Preferred Engine (Sampler)
    if (instrumentDef && (instrumentDef.preferredEngine === 'sampler' || instrumentDef.sampleMap)) {
        const sampler = getSamplerForInstrument(instrumentDef, isMobile);
        if (sampler) return sampler;
    }

    // Operation Virtuoso: Resolve Envelope Bias
    // Defaults to 'normal' if undefined
    const envelopeBias = instrumentDef ? (instrumentDef.envelope_bias || 'normal') : 'normal';

    // 1. Determine Polyphony Cap
    // Reduced for stability: 32 desktop / 12 mobile
    const highPoly = isMobile ? 12 : 32;
    const lowPoly = isMobile ? 6 : 12;

    // 2. Identify Type (from Def or Role)
    let type = instrumentDef ? (instrumentDef.id || instrumentDef.type) : role.toLowerCase();

    // Fix for generic "drum" type in InstrumentLibrary not matching specific synth logic
    if (type === 'drum' || type === 'drums') {
        type = role.toLowerCase(); // Fallback to role (perc_low, perc_mid, etc.)
    }

    // 3. Construct Chain
    // Default is just Synth
    let synth = null;
    let filterNode = null; // Standardized access to filter for automation

    // --- DRUMS ---
    // Check for explicit roles (perc_low, etc) OR keywords
    if (type.includes('kick') || type.includes('timpani') || type.includes('perc_low')) {
        let attack = 0.001;
        if (envelopeBias === 'snap') attack = 0.0001; // Force click
        if (envelopeBias === 'lazy') attack = 0.01;

        synth = new Tone.MembraneSynth({
            pitchDecay: 0.02,
            octaves: 4,
            oscillator: { type: "sine" },
            // Ultra-fast attack for snappy kick
            envelope: { attack: attack, decay: 0.2, sustain: 0.01, release: 1.0 },
            volume: 2
        });
    }
    else if (type.includes('snare') || type.includes('clap') || type.includes('perc_mid')) {
        synth = new Tone.NoiseSynth({
            noise: { type: 'white' },
            // Decay adjusted, Release important to not cut off tail
            envelope: { attack: 0.001, decay: 0.2, sustain: 0 },
            volume: 2 // Increased from 0 to 2 for punch
        });
    }
    else if (type.includes('hat') || type.includes('shaker') || type.includes('perc_high')) {
        synth = new Tone.MetalSynth({
            harmonicity: 5.1,
            modulationIndex: 32,
            resonance: 4000,
            // Longer release to prevent 'choked' sound on short notes
            envelope: { attack: 0.001, decay: 0.1, release: 0.05 }
        });
        synth.volume.value = -10;
    }

    // --- GUITARS ---
    else if (type.includes('dist_guitar') || type.includes('lead_guitar')) {
        const poly = new Tone.PolySynth(Tone.MonoSynth, {
            maxPolyphony: lowPoly,
            oscillator: { type: "fmsawtooth", modulationIndex: 3 },
            // Sharpened Attack (0.005) to sync with beat
            envelope: { attack: 0.005, decay: 0.3, sustain: 0.7, release: 0.2 }
        });

        // Distortion (Sound Design)
        // Allow mixTraits to override distortion gain
        let distAmount = 1.0;
        if (instrumentDef && instrumentDef.mixTraits && instrumentDef.mixTraits.distortion !== undefined) {
            distAmount = instrumentDef.mixTraits.distortion;
        }

        const dist = new Tone.Distortion(distAmount);
        // Brighter Filter
        const filter = new Tone.Filter(4000, "lowpass"); // Opened up from 3000
        poly.chain(dist, filter);

        // Wrap synth with its chain
        synth = new SynthWrapper(poly, [dist, filter]);
        filterNode = filter;   // Standardized
    }

    // --- ORCHESTRAL IMPROVEMENTS ---
    // Flute/Woodwinds -> Triangle/Sine mix
    else if (type.includes('flute') || type.includes('woodwind') || type.includes('clarinet')) {
        const poly = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: highPoly,
            oscillator: { type: "triangle" }, // Softer than default triangle
            envelope: { attack: 0.1, decay: 0.2, sustain: 0.8, release: 0.5 },
            volume: -15 // Lowered to prevent distortion
        });
        // Optional: Filter for breathiness control?
        const filter = new Tone.Filter(4000, "lowpass");
        poly.connect(filter);

        synth = new SynthWrapper(poly, [filter]);
        filterNode = filter;
    }
    // Brass -> AMSynth (Better for Brass than MonoSynth) or FM
    else if (type.includes('brass') || type.includes('horn') || type.includes('trumpet')) {
        // Upgrade: Use AMSynth for richer brass texture (Sawtooth + Amplitude Mod)
        // MonoSynth Saw was a bit thin.
        const poly = new Tone.PolySynth(Tone.AMSynth, {
            maxPolyphony: highPoly,
            harmonicity: 3,
            modulation: { type: "square" },
            oscillator: { type: "sawtooth" }, // Bright base
            envelope: { attack: 0.05, decay: 0.2, sustain: 0.7, release: 0.5 },
            modulationEnvelope: { attack: 0.05, decay: 0.2, sustain: 0.8, release: 0.5 },
            volume: -12
        });

        const filter = new Tone.Filter(3000, "lowpass");
        poly.connect(filter);

        synth = new SynthWrapper(poly, [filter]);
        filterNode = filter;
    }
    // Strings -> FM String (Refined)
    else if (type.includes('fm_strings') || type === 'violin' || type === 'cello' || type.includes('string')) {
        // Upgrade: Refined FMSynth parameters for "bowed" feel
        const poly = new Tone.PolySynth(Tone.FMSynth, {
            maxPolyphony: highPoly,
            harmonicity: 1.0, // Fundamental dominance
            modulationIndex: 3.5,
            oscillator: { type: "sawtooth" }, // Richer carrier than sine
            modulation: { type: "sine" },
            // Slow attack for strings
            envelope: { attack: 0.3, decay: 0.5, sustain: 0.8, release: 1.5 },
            modulationEnvelope: { attack: 0.3, decay: 0.5, sustain: 0.8, release: 1.5 },
            volume: -14
        });
        // Filter for "Sul Ponticello" or darkness
        const filter = new Tone.Filter(5000, "lowpass");
        poly.connect(filter);

        synth = new SynthWrapper(poly, [filter]);
        filterNode = filter;
    }

    // --- HARDWARE EMULATION ---
    else if (type === 'hardware_poly' || type === 'prophet_6' || type === 'ob_6') {
         // Emulate Analog Polysynth (Warm Saw)
         const poly = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: highPoly,
            oscillator: { type: "fatsawtooth", count: 3, spread: 20 },
            envelope: { attack: 0.05, decay: 0.2, sustain: 0.6, release: 1.0 },
            volume: -8 // Boost slightly
        });
        const filter = new Tone.Filter(3000, "lowpass");
        const chorus = new Tone.Chorus(2.5, 3.5, 0.5).start();
        poly.chain(filter, chorus);

        synth = new SynthWrapper(poly, [filter, chorus]);
        filterNode = filter;
    }
    else if (type === 'hardware_mono' || type === 'subsequent_37' || type === 'sh_01a') {
         // Emulate Analog Monosynth (Fat Pulse/Saw)
         const poly = new Tone.PolySynth(Tone.MonoSynth, {
            maxPolyphony: 1, // Strict Mono
            oscillator: { type: "pulse", width: 0.4 },
            filterEnvelope: {
                attack: 0.01, decay: 0.2, sustain: 0.4, release: 0.5,
                baseFrequency: 100, octaves: 4, exponent: 2
            },
            envelope: { attack: 0.01, decay: 0.3, sustain: 0.6, release: 0.5 },
            volume: -5
        });
        const dist = new Tone.Distortion(0.2); // Slight saturation
        poly.connect(dist);

        synth = new SynthWrapper(poly, [dist]);
    }

    // --- SYNTHS ---
    else if (type.includes('supersaw') || type === 'supersaw_lead') {
        let attack = 0.005;
        if (envelopeBias === 'snap') attack = 0.001;
        if (envelopeBias === 'lazy') attack = 0.05;
        if (envelopeBias === 'bloom') attack = 0.2;

        const poly = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: lowPoly,
            oscillator: { type: "fatsawtooth", count: 3, spread: 30 },
            // Faster attack (0.005) for leads
            envelope: { attack: attack, decay: 0.1, sustain: 0.5, release: 1 },
            volume: -6 // Explicit volume boost for software synth
        });

        // Add Chorus for wider stereo image
        const chorus = new Tone.Chorus({
            frequency: 1.5,
            delayTime: 3.5,
            depth: 0.7,
            spread: 180
        }).start();

        poly.connect(chorus);
        synth = new SynthWrapper(poly, [chorus]);
    }
    else if (type === 'pluck_synth' || type.includes('pluck')) {
         // Dedicated Pluck Logic
         const poly = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: highPoly,
            oscillator: { type: "fmsawtooth", modulationIndex: 2 },
            envelope: { attack: 0.005, decay: 0.2, sustain: 0.1, release: 1.0 },
            volume: -8
        });
        const filter = new Tone.Filter(3000, "lowpass");
        const delay = new Tone.FeedbackDelay("8n.", 0.3);
        poly.chain(filter, delay);

        synth = new SynthWrapper(poly, [filter, delay]);
        filterNode = filter;
    }
    else if (type === 'additive_pad' || type.includes('glass')) {
        // Additive Synthesis Simulation using Custom Oscillator Partials
        // Odd harmonics emphasis for hollow/glassy sound
        const poly = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: highPoly,
            oscillator: {
                type: 'custom',
                partials: [1, 0, 0.3, 0, 0.1, 0, 0.05] // 1st, 3rd, 5th, 7th harmonics
            },
            envelope: { attack: 0.1, decay: 2, sustain: 0.5, release: 3 }
        });
        const filter = new Tone.Filter(2000, "lowpass");
        poly.connect(filter);

        synth = new SynthWrapper(poly, [filter]);
        filterNode = filter;
    }
    else if (type.includes('pad')) {
        // Upgrade: FM Pad for evolving texture
        // Replaces simple AMSynth
        const poly = new Tone.PolySynth(Tone.PolySynth, {
            maxPolyphony: highPoly,
            voice: Tone.FMSynth,
            options: {
                harmonicity: 0.5, // Octave down mod
                modulationIndex: 2,
                oscillator: { type: "sawtooth" },
                modulation: { type: "triangle" },
                envelope: { attack: 0.5, decay: 2, sustain: 0.8, release: 3 },
                modulationEnvelope: { attack: 1, decay: 3, sustain: 0.5, release: 3 },
                volume: -15
            }
        });
        const filter = new Tone.Filter(1500, "lowpass");
        poly.connect(filter);

        synth = new SynthWrapper(poly, [filter]);
        filterNode = filter;
    }
    else if (type.includes('bass')) {
        synth = new Tone.PolySynth(Tone.MonoSynth, {
            maxPolyphony: 4,
            oscillator: { type: "fatsawtooth", count: 2, spread: 15 }, // Mild spread for Bass to keep center tight
            filterEnvelope: { attack: 0.001, decay: 0.2, sustain: 0.5, baseFrequency: 200, octaves: 2.6 },
            envelope: { attack: 0.005, decay: 0.2, sustain: 0.5, release: 0.5 }
        });
    }

    // --- DEFAULT ---
    else {
        if (role.includes('harmony') || role.includes('pad')) {
            // PAD Logic
            let attack = 0.1;
            if (envelopeBias === 'bloom') attack = 0.5; // Slow swell
            if (envelopeBias === 'lazy') attack = 0.05;

            // Default Pad: FMSynth
            synth = new Tone.PolySynth(Tone.FMSynth, {
                maxPolyphony: highPoly,
                harmonicity: 1.0,
                modulationIndex: 1.5,
                oscillator: { type: "triangle" },
                envelope: { attack: attack, decay: 1, sustain: 1, release: 2 }
            });
        } else if (role.includes('lead') || role.includes('melody')) {
             // LEAD Logic
             let attack = 0.005;
             if (envelopeBias === 'snap') attack = 0.001;
             if (envelopeBias === 'lazy') attack = 0.03;

             synth = new Tone.PolySynth(Tone.Synth, {
                maxPolyphony: lowPoly,
                oscillator: { type: "fatsawtooth", count: 3, spread: 20 },
                envelope: { attack: attack, decay: 0.2, sustain: 0.5, release: 1 }
            });
        } else {
             // Generic Pluck
             let attack = 0.005;
             if (envelopeBias === 'snap') attack = 0.001;
             if (envelopeBias === 'lazy') attack = 0.02;

            synth = new Tone.PolySynth(Tone.Synth, {
                maxPolyphony: highPoly,
                oscillator: { type: "triangle" },
                // Snappy pluck
                envelope: { attack: attack, decay: 0.1, sustain: 0.3, release: 1 }
            });
        }
    }

    // Attach standardized filter node if it was created
    if (synth && filterNode) {
        synth.filterNode = filterNode;
    }

    return synth;
};
